/**
 * The pocket layout — a port of `PT Mobile Screen.dc.html`.
 *
 * The prototype hard-coded the iOS notch as a 56px spacer inside a device frame;
 * on real hardware we use `env(safe-area-inset-*)` instead, so the same build is
 * correct in a browser, in a Capacitor iOS shell and on an Android device with
 * gesture navigation.
 */

import { h } from '../vdom.js';
import {
  C, MONO, SANS, css, kicker, bar, input, select, button, removeButton, tap,
  alertText, incomeLabel, expenseLabel, expenseText, classLabel, carTypeLabel, gainColor, emptyLine,
  netColor, netBarFill,
  vehicleForm, currencyPicker, monthPicker, datePicker, pageClass, holdingExitDialog, recurringAgainDialog,
  editDialog, costLine, basisPreview, upcomingPanel, adSlot, fullTankToggle, categoryChoices,
  reviewPanel, termsNotice,
  demoBanner, debtChange, quotesAllowed, planLine, logoMark, isPremium,
  guideLink
} from './common.js';
import { whatIfPanel } from './whatif.js';
import { cashKpis, flowCard, transactionsCard, fixedCard } from './cashflow.js';
import { debtKpis, debtChart, debtQueue, debtPlanCard, debtPlans } from './debts.js';
import { investTab } from './investments.js';
import { vehicleTab } from './vehicle.js';
import {
  kpiRow, trendCard, moneyCard, categoriesCard, attentionCard, upcomingCard, reviewCard, whatIfCard, simulatorPage
} from './overview.js';
import {
  CATEGORIES, ASSET_CLASSES, CAR_EXPENSE_TYPES, isAmountOnly
} from '../../../shared/model.js';
import { renderJournal } from './journal.js';
import { renderTaxes } from './taxes.js';
import { budgetsCard, goalsCard } from './goals.js';

const TABS = ['overview', 'cash', 'debt', 'invest', 'car', 'journal', 'tax'];
/**
 * What the "+" offers, per tab.
 *
 * The prototype had one sheet with three fixed forms, so the phone could not
 * add a debt, a holding, a service record or edit the car at all — everything
 * the desktop could do. These lists give each tab its own three, which is as
 * many as fit across a phone, and together they cover every desktop form.
 */
const SHEETS_BY_TAB = {
  overview: ['income', 'expense', 'fuel'],
  cash: ['income', 'expense', 'fuel'],
  debt: ['debt', 'income', 'expense'],
  invest: ['holding', 'income', 'expense'],
  car: ['fuel', 'service', 'vehicle'],
  // The journal has its own inline form; the sheet keeps the ledger shortcuts.
  journal: ['income', 'expense', 'fuel'],
  tax: ['income', 'expense', 'fuel']
};

const sheetsFor = tab => SHEETS_BY_TAB[tab] || SHEETS_BY_TAB.overview;

const panel = (style, ...children) => h('div', {
  style: css({
    background: C.card,
    border: `1px solid ${C.line}`,
    borderRadius: '16px',
    ...style
  })
}, ...children);

export function renderMobile(ctx) {
  const { state } = ctx;

  return h('div', {
    key: 'mobile',
    style: css({
      position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column',
      background: C.bg, color: C.text, fontFamily: SANS, overflow: 'hidden'
    })
  },
    /*
     * These keys are load-bearing, not decoration. The sheet is conditional, so
     * without them the child list shrinks from five to four when it closes and
     * index-based diffing rewrites the sheet's element into the FAB — handler
     * and all. The click that closed the sheet is still bubbling at that point,
     * reaches the rewritten node, and reopens it. Keys make the removal a
     * removal.
     */
    topBar(ctx),
    /*
     * Keyed by tab, not just "the body". One of five renderers occupies this
     * slot at a time and a nav click is what swaps them; with a constant key
     * the outgoing tab's elements are patched into the incoming tab's and
     * inherit its handlers while that click is still bubbling. It also means
     * a new tab opens at the top, which is what you expect of a new tab.
     */
    h('div', {
      key: 'tab-' + state.tab,
      class: 'pt-scroll ' + pageClass(state),
      style: css({
        flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden',
        padding: '14px 16px calc(168px + env(safe-area-inset-bottom, 0px))',
        display: 'flex', flexDirection: 'column', gap: '12px'
      })
    },
      state.user?.isGuest && demoBanner(ctx),
      state.tab === 'overview' && overview(ctx),
      state.tab === 'cash' && cashflow(ctx),
      state.tab === 'debt' && debtTab(ctx),
      state.tab === 'invest' && investments(ctx),
      state.tab === 'car' && vehicle(ctx),
      state.tab === 'journal' && renderJournal({ ...ctx, mobile: true }),
      state.tab === 'tax' && renderTaxes({ ...ctx, mobile: true })
    ),
    state.sheet && addSheet(ctx),
    fab(ctx),
    tabBar(ctx),
    state.holdingExit && holdingExitDialog(ctx),
    state.recurringAgain && recurringAgainDialog(ctx),
    /* One row at a time, whichever kind it is. */
    state.editing && editDialog(ctx)
  );
}

// ---------------------------------------------------------------- top bar ---

function topBar({ state, metrics, t, fmt, actions }) {
  const syncColor = !state.online ? C.danger : state.syncVisible ? C.accentHi : C.accent;

  return h('div', {
    key: 'topbar',
    style: css({
      flex: '0 0 auto',
      padding: 'calc(6px + env(safe-area-inset-top, 0px)) 16px 14px',
      display: 'flex', flexDirection: 'column', gap: '12px',
      borderBottom: `1px solid ${C.line}`, background: C.panel
    })
  },
    h('div', { style: css({ display: 'flex', alignItems: 'center', gap: '8px' }) },
      logoMark(24, { premium: isPremium(state), ring: C.panel }),
      h('div', { style: css({ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }) },
        h('div', {
          style: css({
            fontSize: '15px', fontWeight: 800, fontStyle: 'italic',
            letterSpacing: '-0.5px', lineHeight: 1.1,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', paddingRight: '2px'
          })
        }, t.pages[state.tab][1]),
        h('div', {
          style: css({
            fontFamily: MONO, fontSize: '9px', letterSpacing: '1.3px',
            color: C.faint, marginTop: '3px'
          })
        }, t.pages[state.tab][0])
      ),
      h('div', {
        class: state.syncVisible ? 'pt-pulse' : null,
        style: css({
          width: '8px', height: '8px', borderRadius: '50%', flex: '0 0 8px',
          background: syncColor, marginRight: '2px'
        }),
        title: state.online ? t.shell.synced : t.shell.offline
      }),
      guideLink({ state, t }, {
        flex: '0 0 auto', width: '32px', height: '34px', display: 'flex', alignItems: 'center',
        justifyContent: 'center', border: `1px solid ${C.stroke}`, borderRadius: '17px', background: C.card,
        fontFamily: MONO, fontSize: '13px', fontWeight: 700, color: C.muted
      }, '?'),
      /*
       * Preferences, as the v2 design has it: a ⚙ at the top of every screen.
       * It was the eighth slot of the tab bar, which left each tab about
       * forty-seven pixels; the language switch that sat here lives in
       * Preferences → General now.
       */
      h('div', {
        ...tap(() => actions.openSettings(), t.shell.prefMenuPrefs),
        title: t.shell.prefMenuPrefs,
        style: css({
          flex: '0 0 auto', width: '44px', height: '34px', display: 'flex',
          alignItems: 'center', justifyContent: 'center', border: `1px solid ${C.stroke}`,
          borderRadius: '17px', background: C.card, fontSize: '15px', color: C.textDim, cursor: 'pointer'
        })
      }, '⚙')
    ),

    h('div', { style: css({ display: 'flex', alignItems: 'center', gap: '10px' }) },
      tapTarget('‹', () => actions.stepMonth(-1), t.shell.ariaPrevMonth),
      h('div', {
        style: css({
          flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column',
          alignItems: 'center', gap: '2px'
        })
      },
        h('div', {
          style: css({ fontFamily: MONO, fontSize: '9.5px', letterSpacing: '1.3px', color: C.faint })
        }, `${t.netWorth} · ${fmt.monthName(state.month).toUpperCase()}`),
        h('div', {
          style: css({
            fontFamily: MONO, fontSize: '24px', fontWeight: 700,
            letterSpacing: '-0.5px', color: netColor(metrics.totals.netWorth), lineHeight: 1.1
          })
        }, fmt.money(metrics.totals.netWorth))
      ),
      tapTarget('›', () => actions.stepMonth(1), t.shell.ariaNextMonth)
    )
  );
}

const tapTarget = (glyph, onClick, label) => h('div', {
  ...tap(onClick, label),
  style: css({
    width: '44px', height: '44px', flex: '0 0 44px', display: 'flex',
    alignItems: 'center', justifyContent: 'center', border: `1px solid ${C.stroke}`,
    borderRadius: '12px', background: C.card, color: C.muted,
    fontFamily: MONO, fontSize: '15px', cursor: 'pointer', userSelect: 'none'
  })
}, glyph);

// -------------------------------------------------------------- overview ----

function overview({ metrics, t, fmt, state, actions, inputs }) {
  /* The swap between the overview and its simulator slides, once per swap (see ptSubIn). */
  const motion = state.whatIfFrom === 1 ? 'pt-sub-in' : state.whatIfFrom === -1 ? 'pt-sub-back' : null;
  if (state.whatIfOpen) {
    return h('div', { key: 'simulator', class: motion },
      simulatorPage({ t, actions, compact: true }, whatIfPanel({ state, metrics, t, fmt, actions, compact: true })));
  }
  const ctx = { metrics, t, fmt, state, actions, inputs };

  return h('div', { key: 'dashboard', class: motion, style: css({ display: 'flex', flexDirection: 'column', gap: '12px' }) },
    termsNotice({ state, t, actions, compact: true }),

    /*
     * Top of the screen, not the foot of the account card: it asks for an
     * action, and down there the floating "+" sits on top of its button.
     */
    metrics.missingRates.length > 0 && panel({
      padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '6px',
      border: `1px solid ${C.danger}`
    },
      h('div', {
        style: css({ fontSize: '12.5px', fontWeight: 600, color: C.danger })
      }, t.shell.missingRateTitle),
      h('div', {
        style: css({ fontSize: '11.5px', lineHeight: 1.45, color: C.muted })
      }, t.shell.missingRateBody(metrics.missingRates.join(', '))),
      button(t.shell.setRates, () => actions.openSettings('currencies'), {
        tone: 'muted', style: { fontSize: '10px', padding: '10px 12px', borderRadius: '10px' }
      })
    ),

    state.user && state.user.email && state.user.emailVerified === false && panel({
      padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '8px',
      border: `1px solid ${C.danger}`
    },
      h('div', {
        style: css({ fontSize: '12.5px', fontWeight: 600, color: C.danger })
      }, t.shell.verifyBannerTitle),
      h('div', {
        style: css({ fontSize: '11.5px', lineHeight: 1.45, color: C.muted })
      }, t.shell.verifyBannerBody),
      button(t.shell.verifyResend, actions.resendVerification, {
        tone: 'muted', style: { fontSize: '10px', padding: '10px 12px', borderRadius: '10px' }
      })
    ),

    kpiRow({ ...ctx, compact: true }),
    trendCard({ ...ctx, compact: true }),
    moneyCard({ ...ctx, compact: true }),

    metrics.isEmpty && panel({ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '5px' },
      h('div', {
        style: css({ fontSize: '13px', fontWeight: 700, color: C.textDim })
      }, t.shell.emptyTitle),
      emptyLine(t.shell.emptyBody)
    ),

    attentionCard({ ...ctx, compact: true }),
    categoriesCard({ ...ctx, compact: true }),
    goalsCard({ ...ctx, compact: true }),
    upcomingCard({ ...ctx, compact: true }),
    reviewCard({ ...ctx, compact: true }),
    whatIfCard({ ...ctx, compact: true }),

    accountCard({ state, t, actions }),

    /*
     * The only advertising slot in the app, and the last thing on the page.
     *
     * Never on a screen where somebody is entering money, importing a statement
     * or typing an API key. Returns null for a paid account, for an instance
     * with no publisher id, and until the consent question has been answered.
     */
    adSlot({ state, t, actions })
  );
}

/** Mobile has no sidebar, so account and session controls live at the foot of the dashboard. */
function accountCard({ state, t, actions }) {
  const identity = state.user?.isGuest ? t.shell.guest : (state.user?.email || '—');

  return panel({ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '10px' },
    kicker(t.shell.account, { fontSize: '9px', letterSpacing: '1.2px' }),
    h('div', {
      style: css({
        fontSize: '12px', color: C.textDim,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
      })
    }, identity),
    !state.online && h('div', {
      style: css({ fontSize: '11px', color: C.accentHi, lineHeight: 1.4 })
    }, t.shell.offlineHint),
    /* Gated like the desktop panel — see `syncPanel` there for why. */
    state.syncVisible && state.pending > 0 && h('div', {
      style: css({ fontFamily: MONO, fontSize: '10px', color: C.accentHi })
    }, t.shell.pendingChanges(state.pending)),
    state.user?.hasDiscord && h('div', {
      style: css({ fontFamily: MONO, fontSize: '10.5px', color: C.accent })
    }, state.user.discordHandle),
    state.user?.isGuest && button(t.shell.claim, actions.openClaim, {
      style: { fontSize: '10px', padding: '10px 12px', borderRadius: '10px' }
    }),
    /* Preferences moved to the tab bar — see `tabBar`. A second way in from
       the foot of a scrolling page was the harder one to find, not the
       helpful one. */
    h('div', {
      ...tap(actions.signOut),
      style: css({
        textAlign: 'center', fontFamily: MONO, fontSize: '10px', letterSpacing: '1px',
        color: C.faint, border: `1px solid ${C.stroke}`, borderRadius: '10px',
        padding: '10px', cursor: 'pointer'
      })
    }, t.shell.signOut)
  );
}

const tile = (label, value, color = C.text, sub = null, note = null) =>
  panel({ padding: '13px 14px', display: 'flex', flexDirection: 'column', gap: '6px' },
    kicker(label, { fontSize: '9px', letterSpacing: '1.2px' }),
    h('div', { style: css({ fontFamily: MONO, fontSize: '18px', fontWeight: 700, color }) }, value),
    sub && h('div', { style: css({ fontFamily: MONO, fontSize: '10px', color: C.muted }) }, sub),
    /* Wraps rather than ellipsising: a phone tile is narrow and a line that
       explains the number is worth two lines of height. */
    note && h('div', {
      style: css({ fontSize: '9.5px', color: C.ghost, lineHeight: 1.35 })
    }, note)
  );

// -------------------------------------------------------------- cashflow ----

function cashflow({ metrics, t, fmt, actions, state, inputs }) {
  const ctx = { metrics, t, fmt, state, inputs, actions, compact: true };
  return h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '12px' }) },
    cashKpis(ctx),
    flowCard(ctx),
    budgetsCard({ ...ctx, compact: true }),
    transactionsCard(ctx),
    fixedCard(ctx)
  );
}

const mobileRow = css({
  display: 'flex', alignItems: 'center', gap: '10px',
  padding: '11px 16px', borderBottom: `1px solid ${C.lineSoft}`
});

const sectionHeader = (title, value, color, shaded = false) => h('div', {
  style: css({
    padding: shaded ? '12px 16px' : '13px 16px',
    borderBottom: `1px solid ${C.line}`,
    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
    background: shaded ? C.panel : 'transparent'
  })
},
  h('div', { style: css({ fontSize: '13px', fontWeight: 700 }) }, title),
  h('div', { style: css({ fontFamily: MONO, fontSize: '12px', color }) }, value)
);

// ------------------------------------------------------------------ debt ----

function debtTab({ metrics, t, fmt, state, inputs, actions }) {
  const plans = debtPlans(metrics, state);
  const ctx = { metrics, t, fmt, state, inputs, actions, plans, compact: true };
  return h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '12px' }) },
    debtKpis(ctx),
    debtChart(ctx),
    debtQueue(ctx),
    debtPlanCard(ctx)
  );
}

// ----------------------------------------------------------- investments ----

function investments(ctx) {
  return investTab({ ...ctx, compact: true });
}

// --------------------------------------------------------------- vehicle ----

function vehicle(ctx) {
  /* The car's own details open in the sheet, like every form on the phone. */
  return vehicleTab({ ...ctx, compact: true });
}

// ------------------------------------------------------------- add sheet ----

function addSheet(ctx) {
  const { state, t, inputs, actions, fmt, metrics } = ctx;
  const kinds = sheetsFor(state.tab);
  const kind = kinds.includes(state.sheet) ? state.sheet : kinds[0];

  const field = (name, placeholder, type = 'text', onEnter, onInput) => input(inputs, name, placeholder, {
    type,
    radius: '12px',
    padding: '0 14px',
    style: { height: '46px', fontFamily: MONO, fontSize: '13px' },
    onEnter,
    onInput
  });

  const bigButton = (label, onClick) => h('div', {
    ...tap(onClick),
    style: css({
      height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center',
      borderRadius: '14px', background: C.accent, color: C.bg, fontFamily: MONO,
      fontSize: '12px', fontWeight: 700, letterSpacing: '1px', cursor: 'pointer'
    })
  }, label);

  const column = (...children) => h('div', {
    key: 'form-' + kind,
    style: css({ display: 'flex', flexDirection: 'column', gap: '10px' })
  }, ...children);

  const row = (...children) => h('div', {
    style: css({ display: 'flex', gap: '10px' })
  }, ...children.map(child => h('div', {
    style: css({ flex: 1, minWidth: 0, display: 'flex' })
  }, child)));

  /** Clears the fields and drops the sheet — every form ends the same way. */
  const done = names => {
    inputs.clear(names);
    actions.setSheet(null);
  };

  /**
   * Pairs an amount field with the currency it is being entered in. Passing
   * null renders the picker alone, for forms whose amounts sit on their own row.
   */
  const withCurrency = amountField => h('div', {
    style: css({ display: 'flex', gap: '10px', alignItems: 'stretch' })
  },
    amountField && h('div', { style: css({ flex: 1, minWidth: 0, display: 'flex' }) }, amountField),
    currencyPicker({
      state, actions,
      style: { flex: amountField ? '0 0 92px' : '1 1 auto', height: '46px', fontSize: '13px' }
    })
  );

  const dropdown = (name, options, onChange, value, ariaLabel) => select(inputs, name, options, {
    height: '46px', borderRadius: '12px', padding: '0 12px',
    fontFamily: MONO, fontSize: '13px'
  }, { onChange, value, ariaLabel });

  const forms = {
    income: () => {
      const submit = () => {
        const amount = inputs.number('mIncAmt');
        /* Said, not swallowed -- see the desktop form. */
        if (!amount || amount < 0) return actions.showError(t.shell.amountNeeded);
        actions.addIncome({ label: inputs.value('mIncLabel') || t.phSource, amount });
        done(['mIncLabel', 'mIncAmt']);
      };
      return column(
        field('mIncLabel', t.phSource),
        withCurrency(field('mIncAmt', t.phAmount, 'number', submit)),
        bigButton(t.add, submit)
      );
    },

    expense: () => {
      const submit = () => {
        const amount = inputs.number('mExpAmt');
        if (!amount || amount < 0) return actions.showError(t.shell.amountNeeded);
        actions.addExpense({ label: inputs.value('mExpCat') || 'Other', amount, note: inputs.value('mExpNote') });
        done(['mExpAmt', 'mExpNote']);
      };
      return column(
        dropdown('mExpCat', categoryChoices(state).map(key => ({ value: key, label: expenseLabel(t, key) })), null, undefined, t.shell.categoryLabel),
        field('mExpNote', t.shell.cfNameOut),
        withCurrency(field('mExpAmt', t.phAmount, 'number', submit)),
        bigButton(t.add, submit)
      );
    },

    fuel: () => {
      const submit = () => {
        const odo = inputs.number('mFOdo');
        const volume = inputs.number('mFVol');
        if (!odo || !volume || odo < 0 || volume < 0) return actions.showError(t.shell.fuelNeeded);
        const tank = metrics.vehicle.tank;
        if (tank && volume > tank * 1.15) {
          return actions.showError(t.shell.fillTooBig(fmt.number(Math.round(tank * 10) / 10) + ' ' + fmt.units.volume));
        }
        // The store speaks the canonical unit; the label tells the reader which.
        actions.addFuel({
          odo, gal: volume, cost: inputs.number('mFCost'), date: state.fuelDate,
          full: !state.fuelPartial
        });
        done(['mFOdo', 'mFVol', 'mFCost']);
        actions.setFuelPartial(false);
      };
      return column(
        /* Empty means today, which is what this form always silently assumed. */
        datePicker({
          id: 'm-fuel-date',
          value: state.fuelDate || '',
          onPick: stamp => actions.setPickedDate('fuelDate', stamp),
          label: t.shell.serviceDate,
          title: t.shell.serviceDateTitle,
          note: t.shell.serviceDateNote,
          mobile: true, state, actions, t, fmt
        }),
        field('mFOdo', t.phOdometer + ' (' + fmt.units.distance + ')', 'number'),
        row(
          field('mFVol', fmt.units.volume, 'number'),
          field('mFCost', t.phCost, 'number', submit)
        ),
        withCurrency(null),
        fullTankToggle({
          full: !state.fuelPartial, onToggle: full => actions.setFuelPartial(!full), t, compact: true
        }),
        bigButton(t.fillUp, submit)
      );
    },

    service: () => {
      const submit = () => {
        const amount = inputs.number('mSAmt');
        if (!amount || amount < 0) return actions.showError(t.shell.amountNeeded);
        actions.addCarExpense({
          type: inputs.value('mSType') || 'Maintenance',
          note: inputs.value('mSNote') || '—',
          amount,
          date: state.serviceDate
        });
        done(['mSNote', 'mSAmt']);
      };
      return column(
        dropdown('mSType', CAR_EXPENSE_TYPES.map(key => ({ value: key, label: carTypeLabel(t, key) })), null, undefined, t.shell.categoryLabel),
        datePicker({
          id: 'm-service-date',
          value: state.serviceDate || '',
          onPick: stamp => actions.setPickedDate('serviceDate', stamp),
          label: t.shell.serviceDate,
          title: t.shell.serviceDateTitle,
          note: t.shell.serviceDateNote,
          mobile: true, state, actions, t, fmt
        }),
        field('mSNote', t.phNote),
        withCurrency(field('mSAmt', t.phAmount, 'number', submit)),
        bigButton(t.log, submit)
      );
    },

    debt: () => {
      const submit = () => {
        const balance = inputs.number('mDBal');
        /* Said, not swallowed -- see the desktop form. */
        if (!balance || balance < 0) return actions.showError(t.shell.amountNeeded);
        /* The payment day, as on the desktop form: the calendar export needs it. */
        const payDay = inputs.value('mDDay').trim() ? inputs.number('mDDay') : null;
        if (payDay !== null && (!(payDay >= 1 && payDay <= 31) || Math.round(payDay) !== payDay)) {
          return actions.showError(t.shell.dbPayDayNeeded);
        }
        actions.addDebt({
          name: inputs.value('mDName') || t.phLender,
          balance,
          original: inputs.number('mDOrig') || balance,
          rate: inputs.number('mDRate'),
          payment: inputs.number('mDPay'),
          payDay
        });
        done(['mDName', 'mDBal', 'mDOrig', 'mDRate', 'mDPay', 'mDDay']);
      };
      return column(
        field('mDName', t.phLender),
        row(
          field('mDBal', t.phBalance, 'number'),
          field('mDOrig', t.phOriginal, 'number')
        ),
        row(
          field('mDRate', t.phApr, 'number'),
          field('mDPay', t.phPayment, 'number')
        ),
        field('mDDay', t.shell.dbPayDayF, 'number', submit),
        withCurrency(null),
        bigButton(t.addDebt, submit)
      );
    },

    holding: () => {
      /* Cash takes an amount and nothing else — same rule as the desktop form. */
      const cashEntry = isAmountOnly(state.holdingClass);
      const basis = basisPreview({
        inputs,
        fmt,
        t,
        unitsField: 'mHUnits',
        costField: 'mHCost',
        priceField: 'mHPrice',
        hintField: 'mHBasis',
        /* The currency the row is being entered in, not the account's. */
        currency: state.entryCurrency || metrics.baseCurrency
      });

      const submit = () => {
        const cls = inputs.value('mHClass') || 'Equity / ETF';
        const price = inputs.number('mHPrice');

        /*
         * A quantity and an average cost on a currency balance are numbers that
         * do not exist. Stored as one unit priced at the amount so the value
         * column needs no special case, with both fields off the form.
         */
        if (isAmountOnly(cls)) {
          if (!price) return actions.showError(t.shell.amountNeeded);
          actions.addHolding({
            ticker: inputs.value('mHTicker') || (state.entryCurrency || metrics.baseCurrency),
            name: inputs.value('mHName') || t.shell.cashAmount,
            cls,
            units: 1,
            cost: price,
            price,
            /* Optional: a deposit or savings account earns its way against inflation. */
            yieldPct: inputs.number('mHYield')
          });
          done(['mHTicker', 'mHName', 'mHUnits', 'mHCost', 'mHPrice', 'mHYield']);
          return;
        }

        const units = inputs.number('mHUnits');
        /* Said, not swallowed -- see the desktop form. */
        if (!units || !price) return actions.showError(t.shell.numberNeeded);
        actions.addHolding({
          ticker: inputs.value('mHTicker') || 'NEW',
          name: inputs.value('mHName') || t.phName,
          cls,
          units,
          cost: inputs.number('mHCost') || price,
          price
        });
        done(['mHTicker', 'mHName', 'mHUnits', 'mHCost', 'mHPrice', 'mHYield']);
      };

      return column(
        row(
          field('mHTicker', t.phTicker),
          field('mHName', t.phName)
        ),
        dropdown(
          'mHClass',
          ASSET_CLASSES.map(key => ({ value: key, label: classLabel(t, key) })),
          actions.setHoldingClass,
          /* Seeded from state — see `select`; a rebuilt node otherwise falls
             back to the first option while the form still shows cash fields. */
          state.holdingClass
        ),
        cashEntry
          /*
           * The rate is optional and sits beside the amount: a current account
           * pays nothing and a blank field says exactly that.
           */
          ? row(
              field('mHPrice', t.shell.cashAmount, 'number'),
              field('mHYield', t.phYield, 'number', submit)
            )
          : row(
              field('mHUnits', t.phUnits, 'number', undefined, basis.onInput),
              field('mHCost', t.phAvgCost, 'number', undefined, basis.onInput),
              field('mHPrice', t.phPrice, 'number', submit)
            ),
        cashEntry
          ? h('div', {
              style: css({
                fontSize: '11px', color: C.faint, lineHeight: 1.45,
                display: 'flex', flexDirection: 'column', gap: '4px'
              })
            },
              h('div', null, t.shell.cashNote),
              h('div', { style: css({ color: C.ghost }) }, t.shell.cashYieldNote)
            )
          /*
           * The cost box is a price per unit, on the phone as much as on the
           * desktop. The multiplication shown as it is typed is what stops a
           * total from going into it -- and the note above it answers the
           * question that comes first: which box is which.
           */
          : h('div', {
              style: css({
                display: 'flex', flexDirection: 'column', gap: '6px'
              })
            },
              h('div', {
                style: css({ fontSize: '11px', color: C.faint, lineHeight: 1.45 })
              }, t.shell.holdNote),
              h('div', {
                style: css({ fontSize: '11px', color: C.ghost, lineHeight: 1.45 })
              }, t.shell.holdNoteTotal),
              basis.hint
            ),
        withCurrency(null),
        bigButton(t.add, submit)
      );
    },

    vehicle: () => vehicleForm({ state, metrics, t, fmt, inputs, actions, compact: true })
  };

  const label = {
    income: t.sheet.income,
    expense: t.sheet.expense,
    fuel: t.sheet.fuel,
    service: t.shell.sheetService,
    debt: t.shell.sheetDebt,
    holding: t.shell.sheetHolding,
    vehicle: t.shell.sheetVehicle
  };

  return h('div', {
    key: 'sheet',
    class: 'pt-veil',
    style: css({
      position: 'fixed', inset: 0, zIndex: 40, display: 'flex',
      flexDirection: 'column', justifyContent: 'flex-end', background: 'rgba(4,8,12,.66)'
    })
  },
    h('div', { onclick: () => actions.setSheet(null), style: css({ flex: 1 }) }),
    /*
     * Slides up from off-screen rather than fading in place: it is anchored to
     * the bottom edge, and coming from beyond that edge is the only entrance
     * that explains where it lives. Fixed-position overflow does not scroll the
     * page, so starting below the viewport costs nothing.
     */
    h('div', {
      class: 'pt-sheet', role: 'dialog', 'aria-modal': 'true', 'aria-label': t.quickAdd,
      style: css({
        background: C.card, borderTop: '1px solid ' + C.stroke, borderRadius: '22px 22px 0 0',
        padding: '16px 16px calc(26px + env(safe-area-inset-bottom, 0px))',
        display: 'flex', flexDirection: 'column', gap: '14px',
        maxHeight: '82vh', overflowY: 'auto'
      })
    },
      h('div', { style: css({ display: 'flex', alignItems: 'center', gap: '10px' }) },
        h('div', { style: css({ flex: 1, fontSize: '15px', fontWeight: 700 }) }, t.quickAdd),
        h('div', {
          ...tap(() => actions.setSheet(null), t.shell.close),
          style: css({
            width: '36px', height: '36px', display: 'flex', alignItems: 'center',
            justifyContent: 'center', borderRadius: '12px', border: '1px solid ' + C.stroke,
            color: C.muted, fontFamily: MONO, cursor: 'pointer'
          })
        }, '×')
      ),

      h('div', { style: css({ display: 'flex', gap: '8px' }) },
        ...kinds.map(option => {
          const on = option === kind;
          return h('div', {
            key: option,
            ...tap(() => actions.setSheet(option)),
            'aria-pressed': String(on),
            style: css({
              flex: 1, minWidth: 0, height: '38px', display: 'flex', alignItems: 'center',
              justifyContent: 'center', borderRadius: '11px',
              border: '1px solid ' + (on ? C.accent : C.stroke),
              background: on ? C.line : 'transparent',
              color: on ? C.accent : C.muted,
              fontSize: '12px', fontWeight: 600, cursor: 'pointer',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
            })
          }, label[option]);
        })
      ),

      forms[kind]()
    )
  );
}

// -------------------------------------------------------- chrome: fab/tabs --

function fab({ state, t, actions }) {
  const android = state.platform === 'android';
  return h('div', {
    key: 'fab',
    // Opens on whatever this tab is actually for, rather than always on income.
    /* Named in the reader's language: a Polish screen reader announced "Quick add". */
    ...tap(() => actions.setSheet(state.sheet ? null : sheetsFor(state.tab)[0]), t.quickAdd),
    style: css({
      position: 'fixed',
      right: '16px',
      bottom: `calc(${android ? 96 : 100}px + env(safe-area-inset-bottom, 0px))`,
      zIndex: 30,
      width: android ? '60px' : '56px',
      height: android ? '60px' : '56px',
      borderRadius: android ? '18px' : '28px',
      background: C.accent, color: C.bg, display: 'flex', alignItems: 'center',
      justifyContent: 'center', fontSize: '28px', lineHeight: 1,
      boxShadow: android ? '0 8px 18px rgba(0,0,0,.45)' : '0 10px 24px rgba(0,0,0,.5)',
      cursor: 'pointer', userSelect: 'none'
    })
  }, '+');
}

/*
 * Tab icons, drawn as CSS masks rather than inline SVG.
 *
 * `h()` builds elements with `document.createElement`, which produces an
 * unknown element for `<svg>` rather than a drawable one -- SVG needs
 * `createElementNS`, and teaching the renderer namespaces to decorate a tab bar
 * is a change out of all proportion to the job. A mask paints `currentColor`
 * through the shape instead, which keeps one source per icon and lets the
 * active colour come from the same palette as everything else.
 *
 * Stroked, 24px grid, square joins: the same geometry as the brand mark, which
 * is angular rather than rounded.
 */
const ICON = {
  /* Four panels: the overview is the screen that shows everything at once. */
  overview: '<rect x="3" y="3" width="7.5" height="7.5"/><rect x="13.5" y="3" width="7.5" height="7.5"/>'
    + '<rect x="3" y="13.5" width="7.5" height="7.5"/><rect x="13.5" y="13.5" width="7.5" height="7.5"/>',
  /* In and out, which is what a cashflow screen is. */
  cash: '<path d="M7 20V5"/><path d="M3 9l4-4 4 4"/><path d="M17 4v15"/><path d="M13 15l4 4 4-4"/>',
  /* A balance coming down, one instalment at a time. */
  debt: '<rect x="3" y="4" width="18" height="5"/><rect x="3" y="12" width="12" height="5"/>'
    + '<path d="M3 20h7"/>',
  /* A line that climbs, and the arrow that says which way. */
  invest: '<path d="M3 17l5.5-6 4 3.5L21 6"/><path d="M15 6h6v6"/>',
  car: '<path d="M3 16v-3l2-5h14l2 5v3"/><path d="M3 16h18"/><path d="M3 16v2.5h3V16"/>'
    + '<path d="M18 16v2.5h3V16"/><path d="M7 11.5h10"/>',
  journal: '<rect x="4" y="3" width="16" height="18"/><path d="M8 8h8"/><path d="M8 12h8"/><path d="M8 16h5"/>',
  /* A receipt with a percent sign: what a tax return comes down to. */
  tax: '<path d="M5 3h14v18l-3.5-2-3.5 2-3.5-2L5 21z"/><path d="M9 15l6-6"/>'
    + '<rect x="8.5" y="8.5" width="1.5" height="1.5"/><rect x="14" y="14" width="1.5" height="1.5"/>',
  /*
   * The gear: a ring, a hub and eight teeth.
   *
   * The shape iOS uses, drawn the way this app draws everything else -- flat
   * strokes on a 24px grid, no gradient and no rounded cap -- so it sits beside
   * the other six rather than looking borrowed from another product.
   */
  settings: '<circle cx="12" cy="12" r="6.2"/><circle cx="12" cy="12" r="2.4"/>'
    + '<path d="M12 2.2v3.1"/><path d="M12 18.7v3.1"/><path d="M2.2 12h3.1"/><path d="M18.7 12h3.1"/>'
    + '<path d="M5.1 5.1l2.2 2.2"/><path d="M16.7 16.7l2.2 2.2"/>'
    + '<path d="M18.9 5.1l-2.2 2.2"/><path d="M7.3 16.7l-2.2 2.2"/>'
};

const iconMask = key => {
  const svg = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"'
    + ' stroke="#fff" stroke-width="2" stroke-linecap="square" stroke-linejoin="miter">'
    + ICON[key] + '</svg>';
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
};

const tabIcon = (key, on) => h('div', {
  style: css({
    width: '21px', height: '21px', flex: '0 0 21px',
    background: on ? C.accent : C.muted,
    maskImage: iconMask(key),
    WebkitMaskImage: iconMask(key),
    maskSize: 'contain',
    WebkitMaskSize: 'contain',
    maskRepeat: 'no-repeat',
    WebkitMaskRepeat: 'no-repeat',
    maskPosition: 'center',
    WebkitMaskPosition: 'center'
  })
});

function tabBar({ state, t, actions }) {
  /*
   * The seven tabs. Preferences sat here as an eighth slot until the v2
   * design gave it the ⚙ at the top of every screen (see `topBar`); seven
   * leave each tab a little more room for its label.
   */
  const entries = TABS.map(tab => ({
    key: tab,
    on: state.tab === tab && !state.settingsOpen,
    go: () => actions.setTab(tab)
  }));

  return h('div', {
    key: 'tabbar',
    style: css({
      position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 35,
      background: C.panel, borderTop: `1px solid ${C.line}`,
      display: 'flex', flexDirection: 'column',
      paddingBottom: 'env(safe-area-inset-bottom, 6px)'
    })
  },
    h('div', { style: css({ display: 'flex', alignItems: 'stretch', padding: '7px 4px' }) },
      ...entries.map(entry => {
        const on = entry.on;
        return h('div', {
          key: entry.key,
          ...tap(entry.go, t.nav[entry.key]),
          'aria-current': on ? 'page' : null,
          style: css({
            flex: 1, minWidth: 0, minHeight: '50px', display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: '4px', borderRadius: '13px',
            /*
             * The active tab is marked by its icon and its label going accent,
             * not by a filled tile behind it. Seven tiles at this width left
             * about fifty pixels each, and a background on one of them made the
             * bar look like a row of buttons with one stuck down.
             */
            background: on ? C.lineSoft : 'transparent',
            cursor: 'pointer', padding: '2px 0'
          })
        },
          tabIcon(entry.key, on),
          h('div', {
            style: css({
              fontSize: '8.5px', fontWeight: 600, letterSpacing: '.1px',
              color: on ? C.accent : C.faint, maxWidth: '100%', overflow: 'hidden',
              textOverflow: 'ellipsis', whiteSpace: 'nowrap'
            })
          }, t.nav[entry.key])
        );
      })
    )
  );
}
